"use client";

import { useEffect, useState } from "react";
import { useTranslations } from "next-intl";
import { Text } from "@leabankab/component-library";
import {
	CheckboxConsentField,
	ConfirmationButton,
	FormContainer,
} from "@/components/form";
import { useCountryConfig } from "@/config/country-context";
import { useSubmitTask } from "@/hooks";
import { renderByProcess } from "@/shared/render-by-process";
import { TaskType } from "@/types";
import CreditCardSummaryComponent from "./components/credit-summary";
import LoanSummaryComponent from "./components/loan-summary";
import { useSummaryForm } from "./hooks/use-summary-form";
import {
	CreditCardSummaryPageContext,
	LoanSummaryPageContext,
	LoanSummaryPagePayload,
	SummaryPageContext,
	SummaryPagePayload,
} from "./types";

interface Props {
	context: SummaryPageContext;
	taskId: string;
}

const mapSummaryFormToPayload = (
	body: SummaryPagePayload,
	campaignId?: string
) => ({
	...body,
	application: {
		campaignId,
	},
});

const SummaryPage = ({ context, taskId }: Props) => {
	const t = useTranslations("pages.SummaryPage");
	const { rules } = useCountryConfig();

	const methods = useSummaryForm().useForm;
	const { handleSubmit } = methods;

	const submit = useSubmitTask({
		taskId,
		processType: context.process,
		taskType: TaskType.SummaryPage,
	});

	const [campaignId] = useState<string | undefined>(() => {
		if (typeof window === "undefined") {
			return undefined;
		}

		try {
			const sp = new URLSearchParams(window.location.search);
			return (
				sp.get("utm_campaign") ||
				window.sessionStorage.getItem("campaignId") ||
				undefined
			);
		} catch {
			return undefined;
		}
	});

	useEffect(() => {
		if (!campaignId) {
			return;
		}

		try {
			window.sessionStorage.setItem("campaignId", campaignId);
		} catch {}
	}, [campaignId]);

	const onSubmit = handleSubmit(async (body: LoanSummaryPagePayload) => {
		await submit(mapSummaryFormToPayload(body, campaignId));
	});

	return (
		<FormContainer
			methods={methods}
			onSubmit={onSubmit}
			getPayloadPreview={(values) =>
				mapSummaryFormToPayload(values, campaignId)
			}
		>
			{renderByProcess<LoanSummaryPageContext, CreditCardSummaryPageContext>(
				context,
				{
					loan: (context) => <LoanSummaryComponent context={context} />,
					credit: (context) => <CreditCardSummaryComponent {...context} />,
					salesFinance: (context) => (
						<LoanSummaryComponent context={context} hideDebts />
					),
					myMoney: (context) => <LoanSummaryComponent context={context} />,
				}
			)}

			<CheckboxConsentField name="marketingConsent" label={t("checkbox")} />

			{rules.saveAgreementNotice && (
				<Text textStyle="body-sm">{t("saveAgreementNotice")}</Text>
			)}

			<ConfirmationButton label={t("button")} type="submit" />
		</FormContainer>
	);
};

export default SummaryPage;
